import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
	createBroadcastRequest,
	setAwayLogo,
	setAwayTeam,
	setFixtureDate as setFixtureDateAction,
	setHomeLogo,
	setHomeTeam,
	setLocation,
	setVenue,
} from "../../redux/broadcast/broadcast.actions";
import {
	selectAwayLogo,
	selectAwayTeam,
	selectBroadcastId,
	selectHomeLogo,
	selectHomeTeam,
	selectLocation,
	selectVenue,
} from "../../redux/broadcast/broadcast.selectors";
import FixturePicker, { type Fixture } from "./fixture-picker";

type Step = "picking" | "confirming";

const NewBroadcast = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const homeTeam = useSelector(selectHomeTeam);
	const awayTeam = useSelector(selectAwayTeam);
	const homeLogo = useSelector(selectHomeLogo);
	const awayLogo = useSelector(selectAwayLogo);
	const venue = useSelector(selectVenue);
	const location = useSelector(selectLocation);
	const broadcastId = useSelector(selectBroadcastId);

	const [step, setStep] = useState<Step>("picking");
	const [submitted, setSubmitted] = useState(false);
	const [fixtureId, setFixtureId] = useState<number | undefined>(undefined);
	const [fixtureDate, setFixtureDate] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (broadcastId && submitted) navigate("/broadcast");
	}, [broadcastId, submitted, navigate]);

	const handleSelectFixture = (fixture: Fixture) => {
		dispatch(setHomeTeam(fixture.homeTeam));
		dispatch(setAwayTeam(fixture.awayTeam));
		dispatch(setHomeLogo(fixture.homeLogo));
		dispatch(setAwayLogo(fixture.awayLogo));
		dispatch(setVenue(fixture.venue));
		dispatch(setLocation(fixture.location));
		dispatch(setFixtureDateAction(fixture.date));
		setFixtureId(fixture.fixtureId);
		setFixtureDate(fixture.date);
		setStep("confirming");
	};

	const handleManual = () => {
		dispatch(setHomeTeam(""));
		dispatch(setAwayTeam(""));
		dispatch(setHomeLogo(""));
		dispatch(setAwayLogo(""));
		dispatch(setVenue(""));
		dispatch(setLocation(""));
		dispatch(setFixtureDateAction(""));
		setFixtureId(undefined);
		setFixtureDate(undefined);
		setStep("confirming");
	};

	const handleSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (homeTeam && awayTeam) {
				dispatch(createBroadcastRequest({ homeTeam, awayTeam, homeLogo, awayLogo, venue, location, fixtureId, fixtureDate }));
				setSubmitted(true);
			}
		},
		[homeTeam, awayTeam, homeLogo, awayLogo, venue, location, fixtureId, fixtureDate, dispatch],
	);

	if (step === "picking") {
		return <FixturePicker onSelect={handleSelectFixture} onManual={handleManual} />;
	}

	return (
		<div>
			<button onClick={() => setStep("picking")}>← Back to fixtures</button>
			<div>Confirm broadcast details</div>
			<form onSubmit={handleSubmit}>
				<label htmlFor="team-1">Home team</label>
				<input
					id="team-1"
					value={homeTeam}
					onChange={(e) => dispatch(setHomeTeam(e.target.value))}
				/>
				<label htmlFor="team-2">Away team</label>
				<input
					id="team-2"
					value={awayTeam}
					onChange={(e) => dispatch(setAwayTeam(e.target.value))}
				/>
				<button disabled={homeTeam === "" || awayTeam === ""} type="submit">
					Start broadcast
				</button>
			</form>
		</div>
	);
};

export default NewBroadcast;
